import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CircleX,
  CreditCard,
  ExternalLink,
  Info,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import type {
  GetPatientSubscriptionResponse,
  Invoice,
  RegionId,
  Subscription,
  SubscriptionPlan,
} from '@snootsvet/shared'
import {
  formatCardBrand,
  formatCardNumber,
  formatCurrency,
  formatInstantDate,
  formatInterval,
  resolveDateFormat,
  SUBSCRIPTION_PLAN_LABELS,
  TERMINATED_SUBSCRIPTION_STATUSES,
} from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Pagination } from '@/frontend/components/pagination'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/frontend/components/ui/accordion'
import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Input } from '@/frontend/components/ui/input'
import { Label } from '@/frontend/components/ui/label'
import { Spinner } from '@/frontend/components/ui/spinner'
import { PaymentMethodPicker } from '@/frontend/features/orders/payment-method-picker'
import { InvoiceList } from '@/frontend/features/patient/invoice-list'
import { MembershipCancellationForm } from '@/frontend/features/patient/membership-cancellation.form'
import {
  FleaWormingPlanBadge,
  PatientMembershipAddons,
  PatientMembershipPrimaryDetails,
} from '@/frontend/features/patient/patient-membership-details'
import { useGetPatientSubscriptionUpgrade } from '@/frontend/hooks/api/patients/use-get-patient-subscription-upgrade'
import { usePostPatientSubscriptionUpgrade } from '@/frontend/hooks/api/patients/use-post-patient-subscription-upgrade'
import { useUpdatePatient } from '@/frontend/hooks/api/patients/use-update-patient'
import { useUpdatePatientSubscription } from '@/frontend/hooks/api/patients/use-update-patient-subscription'
import { useUpdateSubscriptionPaymentMethod } from '@/frontend/hooks/api/patients/use-update-subscription-payment-method'
import { useListPlans } from '@/frontend/hooks/api/plans/use-list-plans'
import { type StripeIdValidationResult, validateStripeId } from '@/frontend/lib/stripe'
import { cn } from '@/frontend/lib/utils'

const INVOICES_PER_PAGE = 5

/** Membership management surface for plan upgrades, addons, invoices, and Stripe linkage. */
export function PatientMembershipDialog({
  patientId,
  memberId,
  open,
  onOpenChange,
  memberEmail,
  memberRegion,
  subscription,
  invoices,
}: {
  patientId: string
  memberId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  memberEmail: string
  memberRegion: RegionId
  subscription: GetPatientSubscriptionResponse
  invoices: Invoice[]
}) {
  const [panel, setPanel] = useState<'membership' | 'cancellation'>('membership')
  const [cancelling, setCancelling] = useState(false)

  function handleOpenChange(nextOpen: boolean) {
    if (cancelling) return
    if (!nextOpen) setPanel('membership')
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-3 overflow-hidden sm:max-w-2xl">
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle>
            {panel === 'membership' ? 'Manage membership' : 'Cancel membership'}
          </DialogTitle>
          {panel === 'cancellation' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              disabled={cancelling}
              onClick={() => setPanel('membership')}
            >
              <ArrowLeft /> Back to membership
            </Button>
          )}
        </DialogHeader>
        {panel === 'membership' ? (
          <div className="min-h-0 overflow-y-auto">
            <PatientMembershipPanel
              patientId={patientId}
              memberId={memberId}
              memberEmail={memberEmail}
              memberRegion={memberRegion}
              subscription={subscription}
              invoices={invoices}
              onViewCancellation={() => setPanel('cancellation')}
            />
          </div>
        ) : (
          <MembershipCancellationForm
            patientId={patientId}
            memberEmail={memberEmail}
            memberRegion={memberRegion}
            onPendingChange={setCancelling}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PatientMembershipPanel({
  patientId,
  memberId,
  memberEmail,
  memberRegion,
  subscription,
  invoices,
  onViewCancellation,
}: {
  patientId: string
  memberId: string
  memberEmail: string
  memberRegion: RegionId
  subscription: GetPatientSubscriptionResponse
  invoices: Invoice[]
  onViewCancellation: () => void
}) {
  return (
    <div className="space-y-5">
      <SubscriptionIdControl
        patientId={patientId}
        stripeSubscriptionId={subscription.stripeSubscriptionId}
        stripeSubscriptionUrl={subscription.stripeSubscriptionUrl}
      />

      {subscription.status ? (
        <>
          <section className="space-y-2 border-t pt-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-headline-sm">Membership details</h2>
              {!TERMINATED_SUBSCRIPTION_STATUSES.has(subscription.status) &&
                !subscription.cancelAt && (
                  <Button variant="ghost-destructive" size="sm" onClick={onViewCancellation}>
                    <CircleX aria-hidden />
                    Cancel membership
                  </Button>
                )}
            </div>
            <PatientMembershipPrimaryDetails
              subscription={subscription}
              pendingUpgradeInvoiceUrl={
                invoices.find((invoice) => invoice.pendingSubscriptionUpdate)?.dashboardInvoiceUrl
              }
            />
          </section>

          <SubscriptionPaymentMethod
            key={subscription.stripeSubscriptionId}
            patientId={patientId}
            memberId={memberId}
            subscription={subscription}
            matchesMemberStripeCustomerId={subscription.matchesMemberStripeCustomerId}
          />

          <section className="space-y-2 border-t pt-4">
            <h2 className="text-headline-sm">Addons</h2>
            <AddonsSection
              patientId={patientId}
              subscription={subscription}
              memberRegion={memberRegion}
            />
          </section>

          {subscription.availableUpgrades.length > 0 && (
            <section className="space-y-2 border-t pt-4">
              <h2 className="text-headline-sm">Upgrades</h2>
              <UpgradesSection
                patientId={patientId}
                memberRegion={memberRegion}
                availableUpgrades={subscription.availableUpgrades}
                pendingUpdate={subscription.pendingUpdate}
              />
            </section>
          )}

          <section className="border-t pt-4">
            <Accordion
              defaultValue={invoices.some((invoice) => invoice.overdue) ? ['history'] : []}
            >
              <AccordionItem value="history">
                <AccordionTrigger className="items-center gap-2 py-0 text-headline-sm">
                  Invoice history
                  <Badge variant="secondary" size="sm">
                    {invoices.length}
                  </Badge>
                </AccordionTrigger>
                <AccordionContent className="pt-3">
                  <InvoicesSection
                    invoices={invoices}
                    patientId={patientId}
                    memberEmail={memberEmail}
                    memberRegion={memberRegion}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </section>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {subscription.stripeSubscriptionId === null
            ? 'No subscription linked.'
            : 'Subscription details and invoice history are unavailable.'}
        </p>
      )}
    </div>
  )
}

function SubscriptionPaymentMethod({
  patientId,
  memberId,
  subscription,
  matchesMemberStripeCustomerId,
}: {
  patientId: string
  memberId: string
  subscription: Subscription
  matchesMemberStripeCustomerId: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const update = useUpdateSubscriptionPaymentMethod(patientId)
  const card = subscription.paymentMethod
  const terminated = TERMINATED_SUBSCRIPTION_STATUSES.has(subscription.status)

  return (
    <section className="space-y-3 border-t pt-4">
      <h2 className="text-headline-sm">Payment method</h2>
      {!editing && (
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-sm">
            <CreditCard className="size-3 shrink-0 text-muted-foreground" aria-hidden />
            {card ? (
              <>
                <span className="font-medium">{formatCardBrand(card)}</span>
                <span className="text-muted-foreground">{formatCardNumber(card)}</span>
                <span className="text-muted-foreground">
                  Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                </span>
              </>
            ) : (
              'Card not available'
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={terminated}
            onClick={() => {
              setSelectedId(card?.id ?? null)
              update.reset()
              setEditing(true)
            }}
          >
            Update card
          </Button>
        </div>
      )}
      <AlertDialog open={editing && !matchesMemberStripeCustomerId} onOpenChange={setEditing}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update card in Stripe</AlertDialogTitle>
            <AlertDialogDescription>
              This subscription belongs to a different Stripe customer than the member. Update its
              card directly in Stripe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {terminated && <p className="text-sm text-muted-foreground">This subscription has ended.</p>}
      {editing && !terminated && matchesMemberStripeCustomerId && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Choose a card for this subscription&apos;s future payments.
          </p>
          <PaymentMethodPicker memberId={memberId} value={selectedId} onChange={setSelectedId} />
          {update.error && (
            <p role="alert" className="text-sm text-destructive">
              {update.error.message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={update.isPending} onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              disabled={!selectedId || selectedId === card?.id || update.isPending}
              onClick={() => {
                if (selectedId)
                  update.mutate(
                    { paymentMethodId: selectedId },
                    { onSuccess: () => setEditing(false) }
                  )
              }}
            >
              {update.isPending ? 'Saving…' : 'Save card'}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

function UpgradesSection({
  patientId,
  memberRegion,
  availableUpgrades,
  pendingUpdate,
}: {
  patientId: string
  memberRegion: RegionId
  availableUpgrades: SubscriptionPlan[]
  pendingUpdate: boolean
}) {
  const preview = useGetPatientSubscriptionUpgrade(patientId)
  const upgrade = usePostPatientSubscriptionUpgrade(patientId)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
  const [pricingRequested, setPricingRequested] = useState(false)
  const dateFormat = resolveDateFormat(null, memberRegion)
  const [attemptToken, setAttemptToken] = useState(() => crypto.randomUUID())

  function handleCheckPricing() {
    setPricingRequested(true)
    preview.refetch()
  }

  /** Resets the completed upgrade state. The token rotates only after success so retries retain Stripe's idempotency protection. */
  function resetUpgradeForm() {
    setSelectedPlan(null)
    setPricingRequested(false)
    setAttemptToken(crypto.randomUUID())
  }

  async function handleUpgrade() {
    if (!selectedPlan) return
    try {
      const result = await upgrade.mutateAsync({ plan: selectedPlan, attemptToken })
      toast.success(
        result.subscription.pendingUpdate
          ? 'Upgrade held pending payment'
          : `Upgraded to ${SUBSCRIPTION_PLAN_LABELS[selectedPlan]}`
      )
      resetUpgradeForm()
    } catch {
      // Displayed inline below.
    }
  }

  if (!pricingRequested) {
    return (
      <div className="mt-3 space-y-3">
        {pendingUpdate && <PendingUpgradeAlert />}
        <div className="flex flex-wrap items-center justify-start gap-2">
          <p className="text-sm text-muted-foreground">
            This subscription is eligible for upgrade to the following plan(s):
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {availableUpgrades.map((plan) => (
              <Badge key={plan} variant="outline" size="xs">
                {SUBSCRIPTION_PLAN_LABELS[plan]}
              </Badge>
            ))}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleCheckPricing}>
          Check upgrade pricing
        </Button>
      </div>
    )
  }

  if (preview.isError) {
    return preview.error.name === 'ConflictError' ? (
      <Alert variant="warning" className="mt-3">
        <Info />
        <AlertTitle>Manual upgrade required</AlertTitle>
        <AlertDescription>{preview.error.message}</AlertDescription>
      </Alert>
    ) : (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{preview.error.message}</AlertDescription>
      </Alert>
    )
  }

  if (preview.isFetching || !preview.data) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }

  const selectedOption = preview.data.options.find((option) => option.plan === selectedPlan) ?? null

  return (
    <div className="text-sm space-y-3">
      {pendingUpdate && <PendingUpgradeAlert />}
      <div className="space-y-2">
        {preview.data.options.map((option) => {
          const isSelected = selectedPlan === option.plan

          return (
            <button
              key={option.plan}
              type="button"
              onClick={() => setSelectedPlan(option.plan)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors',
                isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
              )}
            >
              <div
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-full border',
                  isSelected
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-muted-foreground'
                )}
              >
                {isSelected && <Check className="size-3" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  {SUBSCRIPTION_PLAN_LABELS[option.plan]}
                  <ArrowRight className="size-3 text-muted-foreground" />
                  <span className="tabular-nums">
                    {formatCurrency(option.amount, option.currency)} today
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Renews {formatInstantDate(option.newPeriodEnd, dateFormat)}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      {selectedOption && (
        <div className="space-y-2 rounded-md border bg-muted/40 p-3">
          <p className="text-sm font-semibold">
            {formatCurrency(selectedOption.amount, selectedOption.currency)} will be charged to the
            member&apos;s saved card.
          </p>
          <p className="font-normal">
            If the card charge fails, the member must pay the failed invoice within 7 days in order
            to activate the upgrade.
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {selectedOption.lineItems.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Stripe line items have no stable id
              <li key={index} className="flex justify-between gap-4 text-xs">
                <span>{item.description ?? 'Adjustment'}</span>
                <span className="tabular-nums font-semibold">
                  {formatCurrency(item.amount, selectedOption.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {upgrade.error &&
        (upgrade.error.name === 'ConflictError' ? (
          <Alert variant="warning">
            <Info />
            <AlertTitle>Manual upgrade required</AlertTitle>
            <AlertDescription>{upgrade.error.message}</AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{upgrade.error.message}</AlertDescription>
          </Alert>
        ))}

      <div className="flex justify-end">
        <Button onClick={handleUpgrade} disabled={!selectedPlan || upgrade.isPending}>
          {upgrade.isPending ? 'Upgrading…' : 'Confirm upgrade'}
        </Button>
      </div>
    </div>
  )
}

function PendingUpgradeAlert() {
  return (
    <Alert variant="info">
      <Info />
      <AlertDescription>
        This subscription already has a pending upgrade. Applying a new upgrade will void the old
        upgrade invoice.
      </AlertDescription>
    </Alert>
  )
}

function AddonsSection({
  patientId,
  subscription,
  memberRegion,
}: {
  patientId: string
  subscription: Subscription
  memberRegion: RegionId
}) {
  const { mutateAsync: updateSubscription } = useUpdatePatientSubscription(patientId)
  const { data: plansData, isLoading } = useListPlans()
  const currentAddons = subscription.addons
  const addonProducts = plansData?.addons ?? []
  const availableProducts = addonProducts.filter(
    (product) =>
      !(subscription.hasFleaWormTickPlan && /flea|worm|tick/i.test(product.name)) &&
      !currentAddons.some((addon) => product.prices.some((price) => price.id === addon.priceId))
  )
  const defaultValues = useMemo(
    () => Object.fromEntries(availableProducts.map((product) => [product.id, ''])),
    [availableProducts]
  )
  const addonSchema = useMemo(
    () =>
      z.object(Object.fromEntries(availableProducts.map((product) => [product.id, z.string()]))),
    [availableProducts]
  )
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: addonSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        const existingPriceIds = currentAddons.map((addon) => addon.priceId)
        const newPriceIds = Object.values(value).filter(
          (priceId): priceId is string => typeof priceId === 'string' && priceId !== ''
        )
        await updateSubscription({ addons: [...existingPriceIds, ...newPriceIds] })
        toast.success('Addons updated')
      } catch (error) {
        formApi.setErrorMap({
          onSubmit: {
            form: error instanceof Error ? error.message : 'Failed to update subscription',
            fields: {},
          },
        })
      }
    },
  })

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading addon products...</p>
  if (addonProducts.length === 0)
    return (
      <>
        <FleaWormingPlanBadge subscription={subscription} memberRegion={memberRegion} />
        <PatientMembershipAddons subscription={subscription} />
        <p className="text-sm text-muted-foreground">No addon products are available.</p>
      </>
    )
  if (availableProducts.length === 0)
    return (
      <>
        <FleaWormingPlanBadge subscription={subscription} memberRegion={memberRegion} />
        <PatientMembershipAddons subscription={subscription} />
        <p className="text-sm text-muted-foreground">No additional addons are available.</p>
      </>
    )

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 text-sm">
          <form.GlobalError />
          <FleaWormingPlanBadge subscription={subscription} memberRegion={memberRegion} />
          <PatientMembershipAddons subscription={subscription} />

          {availableProducts.map((product) => {
            const options = [
              { label: 'None', value: '' },
              ...product.prices.map((price) => ({
                label: `${price.nickname ?? formatInterval(price.interval, price.intervalCount)} · ${formatCurrency(price.amount, price.currency)}`,
                value: price.id,
              })),
            ]
            return (
              <form.AppField key={product.id} name={product.id}>
                {(field) => (
                  <field.SelectField label={product.name} placeholder="None" items={options} />
                )}
              </form.AppField>
            )
          })}
        </div>

        <div className="flex justify-end border-t pt-3">
          <form.Subscribe
            selector={(state) => ({ isSubmitting: state.isSubmitting, values: state.values })}
          >
            {({ isSubmitting, values }) => {
              const hasNewSelection = Object.values(values).some(
                (value) => typeof value === 'string' && value !== ''
              )
              return (
                <Button type="submit" disabled={isSubmitting || !hasNewSelection}>
                  {isSubmitting ? 'Saving…' : 'Save addons'}
                </Button>
              )
            }}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}

function InvoicesSection({
  invoices,
  patientId,
  memberEmail,
  memberRegion,
}: {
  invoices: Invoice[]
  patientId: string
  memberEmail: string
  memberRegion: RegionId
}) {
  const [page, setPage] = useState(1)

  if (invoices.length === 0) return <p className="text-muted-foreground">No invoices yet.</p>

  const totalPages = Math.ceil(invoices.length / INVOICES_PER_PAGE)
  const currentPage = Math.min(page, totalPages)
  const pageInvoices = invoices.slice(
    (currentPage - 1) * INVOICES_PER_PAGE,
    currentPage * INVOICES_PER_PAGE
  )

  return (
    <div>
      <InvoiceList
        invoices={pageInvoices}
        patientId={patientId}
        memberEmail={memberEmail}
        memberRegion={memberRegion}
      />
      <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
    </div>
  )
}

function SubscriptionIdControl({
  patientId,
  stripeSubscriptionId,
  stripeSubscriptionUrl,
}: {
  patientId: string
  stripeSubscriptionId: string | null
  stripeSubscriptionUrl: string | null
}) {
  const [inputValue, setInputValue] = useState(stripeSubscriptionId ?? '')
  const updatePatient = useUpdatePatient(patientId)
  const stripeIdError = getStripeSubscriptionIdError(validateStripeId(inputValue, 'subscription'))
  const error = stripeIdError ?? updatePatient.error?.message
  const isDirty = inputValue.trim() !== (stripeSubscriptionId ?? '')

  function handleSave() {
    if (stripeIdError) return
    updatePatient.mutate({ stripeSubscriptionId: inputValue.trim() || null })
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Label htmlFor="stripe-subscription-id" className="shrink-0 text-sm">
          Subscription ID
        </Label>
        <Input
          id="stripe-subscription-id"
          value={inputValue}
          onChange={(event) => {
            updatePatient.reset()
            setInputValue(event.target.value)
          }}
          placeholder="sub_..."
          className="min-w-48 flex-1"
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!!stripeIdError || !isDirty || updatePatient.isPending}
        >
          {updatePatient.isPending ? 'Saving…' : 'Save'}
        </Button>
        {stripeSubscriptionUrl && (
          <Button
            variant="outline"
            size="sm"
            render={<a href={stripeSubscriptionUrl} target="_blank" rel="noopener noreferrer" />}
          >
            View in Stripe
            <ExternalLink />
          </Button>
        )}
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}

function getStripeSubscriptionIdError(result: StripeIdValidationResult): string | null {
  if (result === 'valid') return null
  if (result === 'wrong_entity') {
    return 'That appears to be a Stripe customer ID. Patients should be linked to a Stripe subscription ID beginning with sub_.'
  }
  return 'Enter a Stripe subscription ID beginning with sub_.'
}
